import { Centrifuge, Subscription, SubscriptionState } from 'centrifuge';

import { getMessengerSubscriptionToken, getMessengerToken } from 'src/api';
import { CENTRIFUGO_WS_URL } from 'src/config-global';
import { ChatServerEvent, ChatServerEventData, ChatServerEventName } from 'src/types';
import { isSuccessResponse } from 'src/utils/response-type-guards';

import { ChatRealtimeClient, ChatRealtimeConnectParams, ChatSocketStatus } from './types';

// ----------------------------------------------------------------------
// Centrifugo-backed realtime client. The centrifuge SDK owns reconnect,
// backoff, keepalive and token refresh; this class maps its lifecycle onto
// ChatRealtimeClient and dispatches publication payloads (ChatServerEvent
// envelopes) to subscribers. Per-chat channels are synced via setChannels;
// the user's personal channel (from GET /messenger/token) is subscribed
// automatically for cross-chat events.
// ----------------------------------------------------------------------

type EventDataValidator = (data: Record<string, unknown>) => boolean;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const EVENT_DATA_VALIDATORS: Record<ChatServerEventName, EventDataValidator> = {
  'chat.message.new': (data) => isRecord(data.message),
  'chat.receipts': (data) =>
    typeof data.chat_id === 'string' &&
    typeof data.user_id === 'string' &&
    typeof data.last_read_seq === 'number' &&
    typeof data.last_delivered_seq === 'number',
  'chat.unread': (data) =>
    typeof data.chat_id === 'string' &&
    typeof data.unread_count === 'number' &&
    typeof data.my_last_read_seq === 'number' &&
    typeof data.total_unread === 'number',
  'chat.created': (data) => isRecord(data.chat),
  'chat.updated': (data) => isRecord(data.chat),
};

// Centrifugo protocol errors the client cannot recover from by retrying:
// 101 unauthorized, 102 unknown channel (namespace missing server-side),
// 103 permission denied, 104 method not found, 107 bad request. Retrying these
// would only replay the same rejection on every chat-list update.
const PERMANENT_SUBSCRIBE_CODES = new Set([101, 102, 103, 104, 107]);

const MAX_RESUBSCRIBE_ATTEMPTS = 3;

export class CentrifugoChatClient implements ChatRealtimeClient {
  private centrifuge: Centrifuge | null = null;

  private subscriptions = new Map<string, Subscription>();

  private desiredChannels = new Set<string>();

  private personalChannel: string | null = null;

  private platformId: string | null = null;

  private status: ChatSocketStatus = 'idle';

  private wasConnected = false;

  private connectionGeneration = 0;

  private listeners = new Map<ChatServerEventName, Set<(data: never) => void>>();

  private statusListeners = new Set<(status: ChatSocketStatus) => void>();

  private degradedChannels = new Set<string>();

  private rejectedChannels = new Set<string>();

  private resubscribeAttempts = new Map<string, number>();

  private degradedListeners = new Set<(channels: string[]) => void>();

  connect({ platformId }: ChatRealtimeConnectParams) {
    if (this.platformId === platformId && this.centrifuge) return;
    this.teardown();
    if (!CENTRIFUGO_WS_URL) {
      this.setStatus('closed');
      return;
    }
    this.platformId = platformId;
    this.wasConnected = false;

    // No initial `token`: the SDK calls getToken for the first connect and
    // for every refresh. The X-Platform-Id header is injected by axios from
    // the URL, so the token is always scoped to the active platform.
    const centrifuge = new Centrifuge(CENTRIFUGO_WS_URL, {
      getToken: () => this.fetchConnectionToken(),
    });
    this.centrifuge = centrifuge;

    centrifuge.on('connecting', () => {
      if (this.centrifuge !== centrifuge) return;
      this.setStatus(this.wasConnected ? 'reconnecting' : 'connecting');
    });
    centrifuge.on('connected', () => {
      if (this.centrifuge !== centrifuge) return;
      this.wasConnected = true;
      this.resubscribeAttempts.clear();
      this.clearRetriableDegradation();
      this.subscribePersonalChannel();
      this.syncSubscriptions();
      this.setStatus('open');
    });
    centrifuge.on('disconnected', () => {
      if (this.centrifuge !== centrifuge) return;
      this.setStatus('closed');
    });
    // Publications on server-side subscriptions (channels the backend attaches
    // to the connection itself) arrive at the client level.
    centrifuge.on('publication', (ctx) => {
      if (this.centrifuge !== centrifuge) return;
      this.dispatch(ctx.data);
    });

    centrifuge.connect();
    this.syncSubscriptions();
  }

  disconnect() {
    this.platformId = null;
    this.desiredChannels.clear();
    this.teardown();
    this.setStatus('idle');
  }

  setChannels(channels: string[]) {
    this.desiredChannels = new Set(channels);
    this.syncSubscriptions();
  }

  on<E extends ChatServerEventName>(event: E, callback: (data: ChatServerEventData<E>) => void) {
    let callbacks = this.listeners.get(event);
    if (!callbacks) {
      callbacks = new Set();
      this.listeners.set(event, callbacks);
    }
    callbacks.add(callback as (data: never) => void);
    return () => {
      callbacks?.delete(callback as (data: never) => void);
    };
  }

  onStatusChange(callback: (status: ChatSocketStatus) => void) {
    this.statusListeners.add(callback);
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  onDegradedChange(callback: (channels: string[]) => void) {
    this.degradedListeners.add(callback);
    callback([...this.degradedChannels]);
    return () => {
      this.degradedListeners.delete(callback);
    };
  }

  getStatus() {
    return this.status;
  }

  private async fetchConnectionToken(): Promise<string> {
    const generation = this.connectionGeneration;
    const response = await getMessengerToken();
    // Throwing makes the SDK retry with its own backoff. Expired app tokens
    // are already refreshed inside axios, so a failure here is transient.
    if (!response || !isSuccessResponse(response)) {
      throw new Error('chat: messenger token request failed');
    }

    if (generation !== this.connectionGeneration) {
      throw new Error('chat: messenger token discarded (stale connection)');
    }
    this.personalChannel = response.personal_channel || null;
    this.subscribePersonalChannel();
    return response.token;
  }

  private subscribePersonalChannel() {
    if (this.personalChannel) {
      this.createSubscription(this.personalChannel);
    }
  }

  private createSubscription(channel: string) {
    if (!this.centrifuge) return;

    const existing = this.subscriptions.get(channel);
    if (existing) {
      if (existing.state !== SubscriptionState.Unsubscribed) return;
      if (this.degradedChannels.has(channel)) return;

      const attempts = this.resubscribeAttempts.get(channel) ?? 0;
      if (attempts >= MAX_RESUBSCRIBE_ATTEMPTS) {
        this.markDegraded(channel);
        return;
      }
      this.resubscribeAttempts.set(channel, attempts + 1);
      this.centrifuge.removeSubscription(existing);
      this.subscriptions.delete(channel);
    }

    const subscription = this.centrifuge.newSubscription(channel, {
      getToken: async () => {
        const response = await getMessengerSubscriptionToken({ channel });
        if (!response || !isSuccessResponse(response)) {
          throw new Error('chat: subscription token request failed');
        }
        return response.token;
      },
    });
    subscription.on('publication', (ctx) => this.dispatch(ctx.data));
    subscription.on('subscribed', () => {
      this.resubscribeAttempts.delete(channel);
      this.clearDegraded(channel);
    });
    subscription.on('unsubscribed', (ctx) => {
      if (!PERMANENT_SUBSCRIBE_CODES.has(ctx.code)) return;
      this.rejectedChannels.add(channel);
      this.markDegraded(channel);
    });
    subscription.subscribe();
    this.subscriptions.set(channel, subscription);
  }

  private markDegraded(channel: string) {
    if (this.degradedChannels.has(channel)) return;
    this.degradedChannels.add(channel);
    this.notifyDegraded();
  }

  private clearDegraded(channel: string) {
    this.rejectedChannels.delete(channel);
    if (!this.degradedChannels.delete(channel)) return;
    this.notifyDegraded();
  }

  private clearRetriableDegradation() {
    let changed = false;
    this.degradedChannels.forEach((channel) => {
      if (this.rejectedChannels.has(channel)) return;
      this.degradedChannels.delete(channel);
      changed = true;
    });
    if (changed) this.notifyDegraded();
  }

  private notifyDegraded() {
    const channels = [...this.degradedChannels];
    this.degradedListeners.forEach((listener) => listener(channels));
  }

  private syncSubscriptions() {
    if (!this.centrifuge) return;
    this.desiredChannels.forEach((channel) => this.createSubscription(channel));
    this.subscriptions.forEach((subscription, channel) => {
      if (this.desiredChannels.has(channel) || channel === this.personalChannel) return;
      subscription.unsubscribe();
      this.centrifuge?.removeSubscription(subscription);
      this.subscriptions.delete(channel);
      this.resubscribeAttempts.delete(channel);
      this.clearDegraded(channel);
    });
  }

  private dispatch(data: unknown) {
    if (!isRecord(data) || typeof data.event !== 'string' || !isRecord(data.data)) return;
    const validator = (EVENT_DATA_VALIDATORS as Partial<Record<string, EventDataValidator>>)[
      data.event
    ];
    // Unknown event, or a known event with a malformed/missing payload.
    if (!validator || !validator(data.data)) return;
    const parsed = data as ChatServerEvent;
    this.listeners
      .get(parsed.event)
      ?.forEach((listener) => (listener as (payload: unknown) => void)(parsed.data));
  }

  private setStatus(status: ChatSocketStatus) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  private teardown() {
    this.connectionGeneration += 1;
    if (!this.centrifuge) return;
    const { centrifuge } = this;
    this.centrifuge = null;
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
      centrifuge.removeSubscription(subscription);
    });
    this.subscriptions.clear();
    this.personalChannel = null;
    centrifuge.disconnect();
  }
}
